import { createSelector } from "@reduxjs/toolkit";

// Base selectors with safe defaults
const selectFormState = (state) => state.form || {};

// Form data selectors
export const selectFormData = createSelector(
  [selectFormState],
  (formState) => formState.formData || {}
);

export const selectFormConfig = createSelector(
  [selectFormState],
  (formState) => formState.formConfig || null
);

export const selectSectionData = createSelector(
  [selectFormData, (state, sectionKey) => sectionKey],
  (formData, sectionKey) => formData[sectionKey] || {}
);

// Auto-save selectors
export const selectAutoSaveStatus = createSelector(
  [selectFormState],
  (formState) =>
    formState.autoSaveStatus || {
      isLoading: false,
      lastSaved: null,
      error: null,
      savingSection: null,
    }
);

export const selectIsAutoSaving = createSelector(
  [selectAutoSaveStatus],
  (autoSaveStatus) => autoSaveStatus.isLoading
);

export const selectAutoSaveError = createSelector(
  [selectAutoSaveStatus],
  (autoSaveStatus) => autoSaveStatus.error
);

export const selectLastSavedTime = createSelector(
  [selectAutoSaveStatus],
  (autoSaveStatus) => autoSaveStatus.lastSaved
);

export const selectSavingSection = createSelector(
  [selectAutoSaveStatus],
  (autoSaveStatus) => autoSaveStatus.savingSection
);

// Section notes selectors
export const selectSectionNotes = createSelector(
  [selectFormState],
  (formState) => formState.notes || {} // Changed from sectionNotes to notes
);

export const selectSectionNote = createSelector(
  [selectSectionNotes, (state, sectionId) => sectionId],
  (sectionNotes, sectionId) => sectionNotes[sectionId]
);

export const selectNotesSaveStatus = createSelector(
  [selectFormState],
  (formState) =>
    formState.notesSaveStatus || {
      isLoading: false,
      error: null,
      savingNote: null,
    }
);

export const selectIsNoteSaving = createSelector(
  [selectNotesSaveStatus],
  (notesSaveStatus) => notesSaveStatus.isLoading
);

export const selectNotesSaveError = createSelector(
  [selectNotesSaveStatus],
  (notesSaveStatus) => notesSaveStatus.error
);

// Form loading selectors
export const selectFormLoadStatus = createSelector(
  [selectFormState],
  (formState) =>
    formState.formLoadStatus || {
      isLoading: false,
      error: null,
    }
);

export const selectIsFormLoading = createSelector(
  [selectFormLoadStatus],
  (formLoadStatus) => formLoadStatus.isLoading
);

export const selectFormLoadError = createSelector(
  [selectFormLoadStatus],
  (formLoadStatus) => formLoadStatus.error
);

// Completion status selectors
export const selectSectionCompletionStatus = createSelector(
  [selectFormState],
  (formState) => formState.sectionCompletionStatus || {}
);

export const selectSectionCompletion = createSelector(
  [selectSectionCompletionStatus, (state, sectionId) => sectionId],
  (sectionCompletionStatus, sectionId) => sectionCompletionStatus[sectionId]
);

export const selectOverallCompletion = createSelector(
  [selectFormState],
  (formState) =>
    formState.overallCompletion || {
      totalSections: 0,
      completedSections: 0,
      percentComplete: 0,
    }
);

// Combined selectors for components
export const selectFormLoadingState = createSelector(
  [selectIsFormLoading, selectFormLoadError],
  (isLoading, error) => ({
    isLoading,
    error,
  })
);

export const selectAutoSaveState = createSelector(
  [
    selectIsAutoSaving,
    selectAutoSaveError,
    selectLastSavedTime,
    selectSavingSection,
  ],
  (isLoading, error, lastSaved, savingSection) => ({
    isLoading,
    error,
    lastSaved,
    savingSection,
  })
);

export const selectNotesState = createSelector(
  [selectFormState],
  (formState) => formState.notes || {} // Return notes directly
);

// Complex selectors for progress tracking
export const selectSectionProgressData = createSelector(
  [
    selectFormConfig,
    selectFormData,
    selectSectionNotes,
    selectSectionCompletionStatus,
  ],
  (formConfig, formData, sectionNotes, sectionCompletionStatus) => {
    if (!formConfig?.sections) return [];

    const sections = Array.isArray(formConfig.sections)
      ? formConfig.sections
      : Object.values(formConfig.sections);

    return sections.map((section) => {
      const sectionId = section.id || section.sectionId;
      const sectionKey = `section_${sectionId}`;
      const sectionData = formData[sectionKey] || {};
      const note = sectionNotes[sectionId];
      const completion = sectionCompletionStatus[sectionId];

      // Calculate real-time completion if not available
      let status = "incomplete";
      if (completion?.isComplete) {
        status = "complete";
      } else if (note) {
        status = "submitted_with_note";
      }

      return {
        ...section,
        sectionId,
        sectionKey,
        sectionData,
        note,
        completion,
        status,
      };
    });
  }
);

// Memoized selector for form validation
export const selectFormValidationState = createSelector(
  [selectSectionProgressData],
  (sectionsData) => {
    const totalSections = sectionsData.length;
    const completedSections = sectionsData.filter(
      (section) =>
        section.status === "complete" ||
        section.status === "submitted_with_note"
    ).length;

    const validationErrors = [];
    const incompleteSections = sectionsData.filter(
      (section) => section.status === "incomplete"
    );

    incompleteSections.forEach((section) => {
      const requiredFields =
        section.fields?.filter((field) => field.required) || [];
      const missingFields = requiredFields.filter((field) => {
        const value = section.sectionData[field.id];
        if (field.type === "checkbox") {
          return !Array.isArray(value) || value.length === 0;
        }
        return !value || value.toString().trim() === "";
      });

      if (missingFields.length > 0) {
        validationErrors.push({
          sectionId: section.sectionId,
          sectionTitle: section.title,
          missingFields: missingFields.map((field) => field.label),
        });
      }
    });

    return {
      totalSections,
      completedSections,
      percentComplete:
        totalSections > 0
          ? Math.round((completedSections / totalSections) * 100)
          : 0,
      isValid: validationErrors.length === 0,
      validationErrors,
    };
  }
);

// Performance optimized selectors for specific sections
export const createSectionSelector = (sectionId) =>
  createSelector([selectSectionProgressData], (sectionsData) =>
    sectionsData.find(
      (section) => section.sectionId.toString() === sectionId.toString()
    )
  );

export const createSectionDataSelector = (sectionKey) =>
  createSelector([selectFormData], (formData) => formData[sectionKey] || {});

export const createSectionNoteSelector = (sectionId) =>
  createSelector(
    [selectSectionNotes],
    (sectionNotes) => sectionNotes[sectionId]
  );
